import { useState, useEffect } from "react";
import DetalleLayout from "../../Layouts/DetalleLayout/DetalleLayout";
import Button from "../../components/common/Button/Button";
import Badge from "../../components/common/Badge/Badge";
import InputSwitch from "../../components/common/Inputs/InputSwitch/InputSwitch";
import InputDropdown from "../../components/common/Inputs/InputDropdown/InputDropdown";
import Input from "../../components/common/Inputs/Input/Input";
import { FiEdit3, FiUser, FiSettings } from "react-icons/fi";
import { getApiInstance } from "../../utils/api";
import "./perfilUsuario.scss";
import { useTranslation } from "react-i18next";
import i18n from "../../i8n";

export default function PerfilUsuario({ user }) {
    const { t } = useTranslation();

    const mapearRol = (role) => {
        const roles = {
            1: t('profile.roles.administrator'),
            2: t('profile.roles.user'),
            3: t('profile.roles.supervisor'),
            4: t('profile.roles.manager'),
        };
        return roles[role] || t('profile.roles.user');
    };

    const [usuario, setUsuario] = useState({
        nombre: user?.name?.split(" ")[0] || t('profile.roles.user'),
        apellidos: user?.name?.split(" ").slice(1).join(" ") || "-",
        telefono: "91 234 56 78",
        email: user?.email || "usuario@empresa.com",
        perfil: mapearRol(user?.role) || t('profile.roles.user'),
        acceso: t('profile.access'),
    });

    const [configuracion, setConfiguracion] = useState({
        notificaciones: true,
        idioma: i18n.language,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editandoInfo, setEditandoInfo] = useState(false);
    const [infoTemporal, setInfoTemporal] = useState({ ...usuario });

    const idiomasDisponibles = [
        { value: "es", label: "Español" },
        { value: "en", label: "English" },
        { value: "es-MX", label: "Español (México)" },
    ];

    useEffect(() => {
        setInfoTemporal({ ...usuario });
    }, [usuario]);

    const handleGuardarInfo = async () => {
        try {
            setLoading(true);
            const api = getApiInstance();

            setUsuario({ ...infoTemporal });
            setEditandoInfo(false);
            setLoading(false);
        } catch (err) {
            setError(t('profile.errorUpdating'));
            setLoading(false);
        }
    };

    const handleCancelarEdicion = () => {
        setInfoTemporal({ ...usuario });
        setEditandoInfo(false);
    };

    const handleNotificacionesChange = async (valor) => {
        try {
            const nuevaConfig = { ...configuracion, notificaciones: valor };
            setConfiguracion(nuevaConfig);

            const api = getApiInstance();
        } catch (err) {
            setError(t('profile.errorUpdatingNotifications'));
            setConfiguracion({ ...configuracion });
        }
    };

    const handleIdiomaChange = (selectedOption) => {
        if (selectedOption) {
            const nuevaConfig = {
                ...configuracion,
                idioma: selectedOption.value,
            };
            setConfiguracion(nuevaConfig);
            i18n.changeLanguage(selectedOption.value);
        }
    };

    const renderInformacionUsuario = () => (
        <div className="perfil-usuario__info">
            {!editandoInfo ? (
                <>
                    <div className="perfil-usuario__field">
                        <label>{t('fields.name')}:</label>
                        <span>{usuario.nombre}</span>
                    </div>
                    <div className="perfil-usuario__field">
                        <label>{t('fields.lastName')}:</label>
                        <span>{usuario.apellidos}</span>
                    </div>
                    <div className="perfil-usuario__field">
                        <label>{t('fields.phone')}:</label>
                        <span>{usuario.telefono}</span>
                    </div>
                    <div className="perfil-usuario__field">
                        <label>{t('fields.email')}:</label>
                        <span>{usuario.email}</span>
                    </div>
                    <div className="perfil-usuario__field">
                        <label>{t('fields.profile')}:</label>
                        <Badge variant="primary">{usuario.perfil}</Badge>
                    </div>
                    <div className="perfil-usuario__field">
                        <label>{t('fields.access')}:</label>
                        <span>{usuario.acceso}</span>
                    </div>
                </>
            ) : (
                <div className="perfil-usuario__form">
                    <Input
                        type="text"
                        label={t('fields.name')}
                        value={infoTemporal.nombre}
                        onChange={(e) =>
                            setInfoTemporal({
                                ...infoTemporal,
                                nombre: e.target.value,
                            })
                        }
                    />
                    <Input
                        type="text"
                        label={t('fields.lastName')}
                        value={infoTemporal.apellidos}
                        onChange={(e) =>
                            setInfoTemporal({
                                ...infoTemporal,
                                apellidos: e.target.value,
                            })
                        }
                    />
                    <Input
                        type="tel"
                        label={t('fields.phone')}
                        value={infoTemporal.telefono}
                        onChange={(e) =>
                            setInfoTemporal({
                                ...infoTemporal,
                                telefono: e.target.value,
                            })
                        }
                    />
                    <Input
                        type="email"
                        label={t('fields.email')}
                        value={infoTemporal.email}
                        onChange={(e) =>
                            setInfoTemporal({
                                ...infoTemporal,
                                email: e.target.value,
                            })
                        }
                    />
                    <div className="form-field">
                        <label>{t('fields.profile')}:</label>
                        <span className="readonly">{usuario.perfil}</span>
                        <small className="field-note">
                            {t('profile.fieldNotEditable')}
                        </small>
                    </div>
                    <div className="form-field">
                        <label>{t('fields.access')}:</label>
                        <span className="readonly">{usuario.acceso}</span>
                        <small className="field-note">
                            {t('profile.fieldNotEditable')}
                        </small>
                    </div>
                </div>
            )}
        </div>
    );

    const renderConfiguracion = () => (
        <div className="perfil-usuario__config">
            <div className="config-field">
                <div className="config-field-header">
                    <label>{t('profile.notifications')}</label>
                    <small>{t('profile.notificationsDesc')}</small>
                </div>
                <InputSwitch
                    checked={configuracion.notificaciones}
                    onChange={handleNotificacionesChange}
                />
            </div>

            <div className="config-field">
                <div className="config-field-header">
                    <label>{t('profile.language')}</label>
                    <small>{t('profile.languageDesc')}</small>
                </div>
                <InputDropdown
                    options={idiomasDisponibles}
                    value={
                        idiomasDisponibles.find(
                            (idioma) => idioma.value === configuracion.idioma
                        ) || idiomasDisponibles[0]
                    }
                    onChange={handleIdiomaChange}
                    placeholder={t('profile.selectLanguage')}
                    labelVisible={false}
                    className=""
                />
            </div>
        </div>
    );

    return (
        <DetalleLayout
            title={t('profile.title')}
            subtitle={t('profile.subtitle')}
            backLink="/dashboard"
            loading={loading}
            error={error}
            className="perfil-usuario"
        >
            {error && <div className="error-message">{error}</div>}

            <div className="perfil-usuario__content">
                <div className="perfil-usuario__section">
                    <div className="perfil-usuario__section-header">
                        <div className="section-title">
                            <FiUser className="section-icon" />
                            <h2>{t('profile.userInfo')}</h2>
                        </div>
                        {!editandoInfo ? (
                            <Button
                                variant="outlined"
                                startIcon={<FiEdit3 />}
                                onClick={() => setEditandoInfo(true)}
                            >
                                {t('common.edit')}
                            </Button>
                        ) : (
                            <div className="edit-actions">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleCancelarEdicion}
                                >
                                    {t('common.cancel')}
                                </Button>
                                <Button
                                    variant="filled"
                                    size="small"
                                    onClick={handleGuardarInfo}
                                    loading={loading}
                                >
                                    {t('common.save')}
                                </Button>
                            </div>
                        )}
                    </div>
                    <div className="perfil-usuario__section-content">
                        {renderInformacionUsuario()}
                    </div>
                </div>

                <div className="perfil-usuario__section">
                    <div className="perfil-usuario__section-header">
                        <div className="section-title">
                            <FiSettings className="section-icon" />
                            <h2>{t('profile.configuration')}</h2>
                        </div>
                    </div>
                    <div className="perfil-usuario__section-content">
                        {renderConfiguracion()}
                    </div>
                </div>
            </div>
        </DetalleLayout>
    );
}
